import { useState, useRef, useEffect, ReactNode } from 'react'

export interface SearchableListProps<T> {
  items: T[]
  getKey: (item: T) => string
  filterFn: (item: T, searchTerm: string) => boolean
  label: string
  searchPlaceholder: string
  autoFocus?: boolean
  activeKey?: string
  onItemSelect: (item: T) => void
  renderItem: (item: T, isActive: boolean, keyword: string) => ReactNode
}

export const useSearchableListModel = <T>(props: SearchableListProps<T>) => {
  const [filterStr, setFilterStr] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const activeItemRef = useRef<HTMLLIElement>(null)

  const onSearch = (value: string) => {
    setFilterStr(value || '')
  }

  const trimmedFilter = filterStr.trim()

  const filteredItems =
    trimmedFilter === ''
      ? props.items
      : props.items.filter(item => props.filterFn(item, trimmedFilter))

  const autoFocus = props.autoFocus ?? false

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
    }
  }, [autoFocus])

  // Only bring the active item into view when the list first opens, so that
  // scrolling is not yanked back to it while the user is typing.
  useEffect(() => {
    activeItemRef.current?.scrollIntoView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    trimmedFilter,
    filteredItems,
    onSearch,
    inputRef,
    activeItemRef,
  }
}
